type LovableErrorOptions = {
  mechanism?: "manual" | "onerror" | "unhandledrejection" | "react_error_boundary";
  handled?: boolean;
  severity?: "error" | "warning" | "info";
};

type LovableEvents = {
  captureException?: (
    error: unknown,
    context?: Record<string, unknown>,
    options?: LovableErrorOptions,
  ) => void;
};

declare global {
  interface Window {
    __lovableEvents?: LovableEvents;
    __xenomonsErrorListenersInstalled?: boolean;
  }
}

function asError(value: unknown): { name: string; message: string; stack?: string; cause?: unknown } {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: (value as Error & { cause?: unknown }).cause,
    };
  }
  return { name: "NonError", message: typeof value === "string" ? value : safeStringify(value) };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function gatherEnvContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  return {
    route: window.location.pathname,
    search: window.location.search,
    href: window.location.href,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    dpr: window.devicePixelRatio,
    online: navigator.onLine,
    timestamp: new Date().toISOString(),
    buildMode: import.meta.env.MODE,
  };
}

export function reportLovableError(error: unknown, context: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const info = asError(error);
  const fullContext = {
    source: "react_error_boundary",
    ...gatherEnvContext(),
    errorName: info.name,
    errorMessage: info.message,
    errorStack: info.stack,
    errorCause: info.cause ? safeStringify(info.cause) : undefined,
    ...context,
  };

  // Always log structured info to the console so the stack is visible even
  // without the Lovable overlay.
  // eslint-disable-next-line no-console
  console.error("[Xenomons error]", fullContext);

  // Forward to Sentry (no-op if DSN not configured).
  try {
    void import("@/lib/sentry-browser").then(({ captureClientException }) => {
      captureClientException(error, fullContext);
    });
  } catch {
    /* ignore */
  }

  window.__lovableEvents?.captureException?.(error, fullContext, {
    mechanism: "react_error_boundary",
    handled: false,
    severity: "error",
  });
}

export function formatErrorForClipboard(
  error: unknown,
  context: Record<string, unknown> = {},
): string {
  const info = asError(error);
  const env = gatherEnvContext();
  const lines = [
    `Xenomons Fehler-Report`,
    `Zeit: ${env.timestamp}`,
    `Route: ${env.route}${env.search ?? ""}`,
    `Viewport: ${env.viewport} (dpr ${env.dpr})`,
    `Online: ${env.online}`,
    `UserAgent: ${env.userAgent}`,
    `Build: ${env.buildMode}`,
    ``,
    `Fehler: ${info.name}: ${info.message}`,
  ];
  if (info.stack) lines.push(``, `Stack:`, info.stack);
  if (info.cause) lines.push(``, `Cause:`, safeStringify(info.cause));
  if (Object.keys(context).length) {
    lines.push(``, `Kontext:`, safeStringify(context));
  }
  return lines.join("\n");
}

export async function copyErrorToClipboard(
  error: unknown,
  context: Record<string, unknown> = {},
): Promise<boolean> {
  const text = formatErrorForClipboard(error, context);
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }
  if (typeof document !== "undefined") {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
  return false;
}

export function installGlobalErrorListeners() {
  if (typeof window === "undefined") return;
  if (window.__xenomonsErrorListenersInstalled) return;
  window.__xenomonsErrorListenersInstalled = true;

  window.addEventListener("error", (event) => {
    reportLovableError(event.error ?? event.message, {
      source: "window.onerror",
      filename: (event as ErrorEvent).filename,
      lineno: (event as ErrorEvent).lineno,
      colno: (event as ErrorEvent).colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportLovableError((event as PromiseRejectionEvent).reason, {
      source: "unhandledrejection",
    });
  });
}
